import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { FaceMesh } from '@mediapipe/face_mesh';
import aankhein_kholo        from '../assets/audio/aankhein_kholo.mp3';
import camera_ki_trf_dekho   from '../assets/audio/camera_ki_trf_dekho.mp3';
import camera_straight_eye   from '../assets/audio/camera_straight_eyes_blink_new.mp3';
import ekse_jyada_log        from '../assets/audio/ekse_jyada_log.mp3';
import eyes_blink_2          from '../assets/audio/eyes_blink_2.mp3';
import eyes_blink            from '../assets/audio/eyes_blink.mp3';
import face_clean            from '../assets/audio/face_clean.mp3';
import face_inside_the_frame from '../assets/audio/face_inside_the_frame_new.mp3';
import face_straight         from '../assets/audio/face_straight.mp3';
import good_light            from '../assets/audio/good_light_new.mp3';
import near_far              from '../assets/audio/near_far.mp3';
import points                from '../assets/audio/points_new.mp3';
import specs                 from '../assets/audio/specs_new.mp3';
import { QrCode } from 'lucide-react';
import { WORKER_TOKEN_KEY } from '../constants/tokenKeys';

// ── Landmark indices ─────────────────────────────────────────────────────────
const LEFT_EYE_TOP  = 159, LEFT_EYE_BOT  = 145;
const RIGHT_EYE_TOP = 386, RIGHT_EYE_BOT = 374;
const FACE_LEFT  = 234, FACE_RIGHT = 454;
const FACE_TOP   = 10,  FACE_BOT   = 152;
const NOSE_TIP   = 4;
const LEFT_EAR   = 127, RIGHT_EAR  = 356;

// ── Quality thresholds ────────────────────────────────────────────────────────
const EAR_OPEN        = 0.18;
const EAR_OPEN_STILL  = 0.14;
const EAR_CLOSED      = 0.12;
const FACE_COV_MIN    = 0.05;
const FACE_COV_MAX    = 0.50;
const EDGE_PAD        = 0.04;
const NOSE_X_TOL      = 0.15;
const NOSE_Y_MIN      = 0.20;
const NOSE_Y_MAX      = 0.70;
const TILT_TOL        = 0.10;

// ── Liveness thresholds ───────────────────────────────────────────────────────
const BLINKS_REQUIRED    = 2;
const BLINK_COOLDOWN_MS  = 400;
const DEPTH_THRESHOLD    = 0.04;
const TEXTURE_MIN_STDDEV = 8;

// ── Auto capture ──────────────────────────────────────────────────────────────
// How long every check must keep passing before the photo is taken automatically.
const AUTO_CAPTURE_DELAY_MS = 700;

// ── Guide box proportions  ────────────────────────────
const BOX_X = 0.08;   // was 0.18
const BOX_Y = 0.05;   // was 0.08
const BOX_W = 0.84;   // was 0.64
const BOX_H = 0.88;

// ── Route helpers ──────────────────────────────────────────────────────────
// Admin face-scan flow does not require the blink-liveness check.
function isAdminFaceScan() {
  return (
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/lkart/admin/face-scan')
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function earRatio(lm, t, b, l, r) {
  const v = Math.hypot(lm[t].x - lm[b].x, lm[t].y - lm[b].y);
  const h = Math.hypot(lm[l].x - lm[r].x, lm[l].y - lm[r].y);
  return h > 0 ? v / h : 0;
}

function textureSample(canvas, cx, cy, radius) {
  try {
    const ctx  = canvas.getContext('2d', { willReadFrequently: true });
    const size = radius * 2;
    const { data } = ctx.getImageData(
      Math.max(0, cx - radius), Math.max(0, cy - radius), size, size
    );
    const lumas = [];
    for (let i = 0; i < data.length; i += 4)
      lumas.push(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    if (!lumas.length) return null;
    const mean   = lumas.reduce((s, v) => s + v, 0) / lumas.length;
    const stddev = Math.sqrt(
      lumas.reduce((s, v) => s + (v - mean) ** 2, 0) / lumas.length
    );
    return stddev;
  } catch {
    return null;
  }
}

// ── Face analysis (runs every frame) ─────────────────────────────────────────
function analyseResults(results, isStill = false) {
  const c = {
    faceDetected: false,
    singleFace:   false,
    eyesOpen:     false,
    faceInFrame:  false,
    goodDistance: false,
    faceStraight: false,
    depthOk:      false,
    leftEAR:      0,
    rightEAR:     0,
  };
  const earOpenThreshold = isStill ? EAR_OPEN_STILL : EAR_OPEN;

  if (!results?.multiFaceLandmarks?.length) return c;
  c.faceDetected = true;
  c.singleFace   = results.multiFaceLandmarks.length === 1;
  if (!c.singleFace) return c;

  const lm = results.multiFaceLandmarks[0];

  const leftEAR  = earRatio(lm, LEFT_EYE_TOP,  LEFT_EYE_BOT,  33,  133);
  const rightEAR = earRatio(lm, RIGHT_EYE_TOP, RIGHT_EYE_BOT, 362, 263);
  c.leftEAR  = leftEAR;
  c.rightEAR = rightEAR;
  c.eyesOpen = leftEAR >= earOpenThreshold && rightEAR >= earOpenThreshold;

  const fl = lm[FACE_LEFT], fr = lm[FACE_RIGHT];
  const ft = lm[FACE_TOP],  fb = lm[FACE_BOT];
  c.faceInFrame =
    ft.y >= EDGE_PAD && fb.y <= 1 - EDGE_PAD &&
    fl.x >= EDGE_PAD && fr.x <= 1 - EDGE_PAD;

  const fw = Math.abs(fr.x - fl.x);
  const fh = Math.abs(fb.y - ft.y);
  c.goodDistance = fw * fh >= FACE_COV_MIN && fw * fh <= FACE_COV_MAX;

  const nose = lm[NOSE_TIP];
  const le   = lm[LEFT_EAR], re = lm[RIGHT_EAR];
  c.faceStraight =
    Math.abs(nose.x - 0.5) <= NOSE_X_TOL &&
    nose.y >= NOSE_Y_MIN && nose.y <= NOSE_Y_MAX &&
    Math.abs(le.y - re.y) <= TILT_TOL;

  const earAvgZ = (lm[LEFT_EAR].z + lm[RIGHT_EAR].z) / 2;
  c.depthOk = (earAvgZ - lm[NOSE_TIP].z) >= DEPTH_THRESHOLD;

  return c;
}

const positionOk = (c) =>
  c?.faceDetected &&
  c?.singleFace   &&
  c?.faceInFrame  &&
  c?.goodDistance &&
  c?.faceStraight;

// requiredBlinks defaults to BLINKS_REQUIRED so any existing callers keep working.
const allPassed = (c, blinks, requiredBlinks = BLINKS_REQUIRED) =>
  positionOk(c)           &&
  c?.eyesOpen              &&
  c?.depthOk               &&
  blinks >= requiredBlinks;


const ERROR_AUDIO = {
  noFace:       new Audio(camera_ki_trf_dekho),
  multipleFace: new Audio(ekse_jyada_log),
  eyesOpen:     new Audio(aankhein_kholo),
  faceInFrame:  new Audio(face_inside_the_frame),
  distance:     new Audio(near_far),
  faceStraight: new Audio(face_straight),
  photo:        new Audio(face_clean),         // "photo lag rahi" → face_clean
  blink1:       new Audio(eyes_blink),         // first blink prompt
  blink2:       new Audio(eyes_blink_2),       // second blink prompt
  chashma:      new Audio(specs),              // chashma/specs error
  lightIssue:   new Audio(good_light),         // if you add a light check
  straightEye:  new Audio(camera_straight_eye),
  points:       new Audio(points),
};

Object.values(ERROR_AUDIO).forEach(a => a.load());

const CHECK_META = [
  { key: 'faceDetected', fail: 'camera ki taraf dekho',             audioKey: 'noFace'       },
  { key: 'singleFace',   fail: 'Ek se zyada log frame mein na hon', audioKey: 'multipleFace' },
  { key: 'eyesOpen',     fail: 'Aankhen kholo',                     audioKey: 'eyesOpen'     },
  { key: 'faceInFrame',  fail: 'poora face visible rakho',          audioKey: 'faceInFrame'  },
  { key: 'goodDistance', fail: 'Thoda paas ya door aao',            audioKey: 'distance'     },
  { key: 'faceStraight', fail: 'Face ko seedha rakho',              audioKey: 'faceStraight' },
];

// ── Run FaceMesh on a captured still ─────────────────────────────────────────
function runFaceMeshOnce(dataUri) {
  return new Promise((resolve, reject) => {
    const fm = new FaceMesh({
      locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${f}`,
    });
    fm.setOptions({ maxNumFaces: 5, refineLandmarks: true,
      minDetectionConfidence: 0.9, minTrackingConfidence: 0.9 });
    let settled = false;
    fm.onResults((r) => {
      if (!settled) { settled = true; resolve(analyseResults(r, true)); }
    });
    const img  = new Image();
    img.onload  = () => fm.send({ image: img })
      .catch((e) => { if (!settled) { settled = true; reject(e); } });
    img.onerror = () => {
      if (!settled) { settled = true; reject(new Error('Image load failed')); }
    };
    img.src = dataUri;
  });
}

// ─── Error Banner ─────────────────────────────────────────────────────────────
// requiredBlinks is passed down from the parent so both the parent's gating logic
// and the banner's audio/prompt logic always agree on how many blinks are needed
// (0 on /admin/face-scan, BLINKS_REQUIRED everywhere else).
const ErrorBanner = ({ checks, blinkCount, requiredBlinks = BLINKS_REQUIRED }) => {
  const queueRef          = useRef([]);
  const isSpeakingRef     = useRef(false);
  const debounceTimerRef  = useRef(null);
  const lastAudioKeyRef   = useRef(null);
  const noBlinkTimerRef   = useRef(null);
  const [chashmaError, setChashmaError] = useState(false);

  // ── Play next in queue ─────────────────────────────────────────────
  const playNext = useCallback(() => {
    if (isSpeakingRef.current) return;
    if (queueRef.current.length === 0) return;

    const key   = queueRef.current.shift();
    const audio = ERROR_AUDIO[key];
    if (!audio) { playNext(); return; }

    Object.values(ERROR_AUDIO).forEach(a => { a.pause(); a.currentTime = 0; });

    isSpeakingRef.current = true;
    audio.currentTime     = 0;
    audio.play().catch(() => {
      isSpeakingRef.current = false;
      playNext();
    });

    audio.onended = () => {
      isSpeakingRef.current = false;
      playNext();
    };

    audio.onerror = () => {
      isSpeakingRef.current = false;
      playNext();
    };
  }, []);

  // ── Enqueue — max 1 instance of any key in queue ───────────────────
  const enqueue = useCallback((key) => {
    if (queueRef.current.includes(key)) return;
    queueRef.current.push(key);
    playNext();
  }, [playNext]);

  // ── Stop everything and wipe queue ────────────────────────────────
  const stopAudio = useCallback(() => {
    Object.values(ERROR_AUDIO).forEach(a => { a.pause(); a.currentTime = 0; });
    queueRef.current        = [];
    isSpeakingRef.current   = false;
    lastAudioKeyRef.current = null;
  }, []);

  // ── No-blink watchdog ──────────────────────────────────────────────
  useEffect(() => {
    const needsBlink = requiredBlinks > 0 &&
      positionOk(checks) && checks.depthOk && blinkCount < requiredBlinks;
    const remaining  = requiredBlinks - blinkCount;

    if (needsBlink && remaining === 2) {
      if (!noBlinkTimerRef.current) {
        noBlinkTimerRef.current = setTimeout(() => setChashmaError(true), 6000);
      }
    } else {
      clearTimeout(noBlinkTimerRef.current);
      noBlinkTimerRef.current = null;
      setChashmaError(false);
    }
  }, [checks, blinkCount, requiredBlinks]);

  // ── Cleanup on unmount ─────────────────────────────────────────────
  useEffect(() => () => {
    clearTimeout(noBlinkTimerRef.current);
    clearTimeout(debounceTimerRef.current);
    stopAudio();
  }, [stopAudio]);

  // ── Audio trigger ──────────────────────────────────────────────────
  useEffect(() => {
    const first      = CHECK_META.find(({ key }) => checks[key] === false);
    const depthFail  = checks.faceDetected && checks.singleFace && !checks.depthOk;
    const needsBlink = requiredBlinks > 0 &&
      positionOk(checks) && checks.depthOk && blinkCount < requiredBlinks;
    const remaining  = requiredBlinks - blinkCount;

    let audioKey = null;
    if      (first)                         audioKey = first.audioKey;
    else if (depthFail)                     audioKey = 'photo';
    else if (needsBlink && chashmaError)    audioKey = 'chashma';
    else if (needsBlink && remaining === 2) audioKey = 'blink1';
    else if (needsBlink && remaining === 1) audioKey = 'blink2';

    if (!audioKey) {
      // Correct position — kill current audio, wipe queue, cancel any pending debounce.
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
      stopAudio();
      return;
    }

    // Same error still active — a timer (if any) is already scheduled for it.
    // Do NOT touch it here, or a re-render with the same key (checks is a new
    // object every frame) would cancel the pending play before it ever fires.
    if (audioKey === lastAudioKeyRef.current) return;

    // New error type — cancel whatever was pending for the previous key,
    // then wait 4-5s before playing (in case it's transient).
    clearTimeout(debounceTimerRef.current);
    lastAudioKeyRef.current = audioKey;
    debounceTimerRef.current = setTimeout(() => {
      enqueue(audioKey);
      debounceTimerRef.current = null;
    }, 4500); // between 4000-5000ms

  }, [checks, blinkCount, chashmaError, enqueue, stopAudio, requiredBlinks]);

  return null;
};

const INSTRUCTION_AUDIO = [
  new Audio(points),                  // index 0 — intro
  new Audio(good_light),              // index 1 — tip 1
  new Audio(face_inside_the_frame),   // index 2 — tip 2
  new Audio(camera_straight_eye),     // index 3 — tip 3
  new Audio(specs),                   // index 4 — tip 4
];

// Preload all
INSTRUCTION_AUDIO.forEach(a => a.load());

// ─── Instructions Screen ──────────────────────────────────────────────────────
const InstructionsScreen = ({ onStart }) => {
  const tips = [
    { title: 'Achhi light wali jagah par khade ho .' },
    { title: 'Poora chehra camera frame k andar rakhiye.' },
    { title: 'Camera ki taraf seedha dekhiye aur Aankhen jhapkaiye.' },
    { title: 'Chashma utaar kr photo lijiye' },
  ];

  const [allDone, setAllDone]         = useState(false);
  const [currentTip, setCurrentTip]   = useState(-1);
  const [isReplaying, setIsReplaying] = useState(false);
  const cancelledRef                  = useRef(false);
  const currentAudioRef               = useRef(null);

  const stopCurrent = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current.onended = null;
      currentAudioRef.current.onerror = null;
      currentAudioRef.current = null;
    }
  };

  const playIndex = useCallback((index) => {
    if (cancelledRef.current) return;
    if (index >= INSTRUCTION_AUDIO.length) {
      setAllDone(true);
      setCurrentTip(-1);
      setIsReplaying(false);
      return;
    }

    setCurrentTip(index - 1);

    const audio = INSTRUCTION_AUDIO[index];
    audio.currentTime = 0;
    currentAudioRef.current = audio;

    audio.onended = () => {
      if (!cancelledRef.current) playIndex(index + 1);
    };
    audio.onerror = () => {
      if (!cancelledRef.current) playIndex(index + 1);
    };

    audio.play().catch(() => {
      if (!cancelledRef.current) playIndex(index + 1);
    });
  }, []);

  const handleReplay = useCallback(() => {
    stopCurrent();
    cancelledRef.current = false;
    setAllDone(false);
    setIsReplaying(true);
    setCurrentTip(-1);
    setTimeout(() => playIndex(0), 100);
  }, [playIndex]);

  useEffect(() => {
    cancelledRef.current = false;
    const t = setTimeout(() => playIndex(0), 200);
    return () => {
      clearTimeout(t);
      cancelledRef.current = true;
      stopCurrent();
    };
  }, [playIndex]);

  return (
    <div className="flex flex-col gap-5 px-5 py-6">
      {/* Header */}
      <div className="flex flex-col items-center text-center gap-1">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-1">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </div>
        <p className="text-base font-semibold text-gray-800">Kripya Inn Baaton Ka Dhyan Rakhiye</p>
      </div>

      {/* Tips list */}
      <div className="flex flex-col gap-2">
        {tips.map(({ title }, i) => (
          <div
            key={title}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300
              ${currentTip === i ? 'bg-blue-50 ring-1 ring-blue-300' : 'bg-gray-50'}`}
          >
            <div className={`w-2 h-2 rounded-full flex-shrink-0 transition-all duration-300
              ${currentTip === i ? 'bg-blue-500 animate-pulse' : 'bg-gray-300'}`}
            />
            <p className={`text-sm font-medium leading-none transition-colors duration-300
              ${currentTip === i ? 'text-blue-700' : 'text-gray-800'}`}>
              {title}
            </p>
          </div>
        ))}
      </div>

      {/* Replay button — visible once audio finishes */}
      <div className={`transition-all duration-500 ${allDone ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
        <button
          onClick={handleReplay}
          disabled={isReplaying}
          className="w-full py-3 border border-blue-300 text-blue-600 text-sm font-semibold rounded-2xl
            hover:bg-blue-50 active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          Replay
        </button>
      </div>

      {/* Continue button */}
      <div className={`transition-all duration-500 ${allDone ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
        <button
          onClick={onStart}
          className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-sm font-semibold rounded-2xl transition-all flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
              d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          Continue
        </button>
      </div>

      {!allDone && (
        <p className="text-xs text-center text-gray-400 -mt-2">
          Please listen to the instructions…
        </p>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SelfieImage = ({ onSuccess, onClose, onSubmit, token, setUser, user }) => {
  const [step, setStep] = useState(onSubmit ? 'camera' : 'instructions');
  const [cameraError,   setCameraError]   = useState(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [liveChecks,    setLiveChecks]    = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedBlob,  setCapturedBlob]  = useState(null);
  const [validating,    setValidating]    = useState(false);
  const [isUploading,   setIsUploading]   = useState(false);
  const [uploadStatus,  setUploadStatus]  = useState('');
  const [facingMode,    setFacingMode]    = useState('user');

  // ── Liveness state ──────────────────────────────────────────────────────────
  const [blinkCount, setBlinkCount] = useState(0);
  const [textureOk,  setTextureOk]  = useState(null);

  // On /admin/face-scan, blink liveness is not required. Computed once per mount
  // (route doesn't change mid-flow) and reused everywhere blink gating happens.
  const requiredBlinksRef = useRef(isAdminFaceScan() ? 0 : BLINKS_REQUIRED);
  const requiredBlinks = requiredBlinksRef.current;

  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const fmRef     = useRef(null);
  const rafRef    = useRef(null);
  const faceOutSinceRef = useRef(null);

  // Blink state machine refs
  const blinkStateRef    = useRef('open');
  const blinkCountRef    = useRef(0);
  const lastBlinkTimeRef = useRef(0);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // ── Teardown ────────────────────────────────────────────────────────────────
  const teardown = useCallback(() => {
    if (rafRef.current)   { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks?.().forEach((t) => t.stop());
      streamRef.current._stop?.();
      streamRef.current = null;
    }
    fmRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // ── Reset liveness counters ──────────────────────────────────────────────────
  const resetLiveness = useCallback(() => {
    blinkStateRef.current    = 'open';
    blinkCountRef.current    = 0;
    lastBlinkTimeRef.current = 0;
    faceOutSinceRef.current  = null;
    setBlinkCount(0);
    setTextureOk(null);
  }, []);

  // ── Blink state machine ───────────────────────────────────────────────────────
  const detectBlink = useCallback((checks) => {
    if (!checks.faceDetected || !checks.singleFace) return;
    const avgEAR = (checks.leftEAR + checks.rightEAR) / 2;
    const now    = Date.now();

    if (blinkStateRef.current === 'open' && avgEAR < EAR_CLOSED) {
      blinkStateRef.current = 'closing';
    } else if (blinkStateRef.current === 'closing' && avgEAR >= EAR_OPEN) {
      if (now - lastBlinkTimeRef.current > BLINK_COOLDOWN_MS) {
        blinkCountRef.current    += 1;
        lastBlinkTimeRef.current  = now;
        setBlinkCount(blinkCountRef.current);
      }
      blinkStateRef.current = 'open';
    }
  }, []);

  // ── Build FaceMesh instance ──────────────────────────────────────────────────
  const buildFaceMesh = useCallback((onResults) => {
    const fm = new FaceMesh({
      locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${f}`,
    });
    fm.setOptions({ maxNumFaces: 5, refineLandmarks: true,
      minDetectionConfidence: 0.9, minTrackingConfidence: 0.9 });
    fm.onResults(onResults);
    return fm;
  }, []);

  const onFrame = useCallback((results) => {
    const c = analyseResults(results);
    detectBlink(c);

    // ── Reset blinks if face leaves frame for > 1 second ──
    const faceVisible = c.faceDetected && c.singleFace && c.faceInFrame;
    if (!faceVisible) {
      if (faceOutSinceRef.current === null) {
        faceOutSinceRef.current = Date.now();
      } else if (Date.now() - faceOutSinceRef.current >= 1000) {
        // Face has been out for 1s — reset liveness
        blinkStateRef.current    = 'open';
        blinkCountRef.current    = 0;
        lastBlinkTimeRef.current = 0;
        setBlinkCount(0);
        faceOutSinceRef.current  = null;  // reset timer so it doesn't fire repeatedly
      }
    } else {
      faceOutSinceRef.current = null;  // face is back — clear the timer
    }

    setLiveChecks(c);
  }, [detectBlink]);

  // ── Desktop camera ───────────────────────────────────────────────────────────
  const startDesktopCamera = useCallback(async () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    try {
      const { Camera } = await import('@mediapipe/camera_utils');
      const fm   = buildFaceMesh(onFrame);
      fmRef.current = fm;
      const mpCam = new Camera(videoEl, {
        onFrame: async () => { await fm.send({ image: videoEl }); },
        width: 640, height: 480, facingMode: 'user',
      });
      await mpCam.start();
      streamRef.current = {
        getTracks: () => videoEl.srcObject?.getTracks() ?? [],
        _stop: () => mpCam.stop(),
      };
      setIsCameraReady(true);
      setCameraError(null);
    } catch (err) { setCameraError(err); }
  }, [buildFaceMesh, onFrame]);

  // ── Mobile camera ────────────────────────────────────────────────────────────
  const startMobileCamera = useCallback(async (facing) => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    if (streamRef.current) {
      streamRef.current.getTracks?.().forEach((t) => t.stop());
      streamRef.current._stop?.();
      streamRef.current = null;
    }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setIsCameraReady(false);
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      videoEl.srcObject = stream;
      await new Promise((res, rej) => {
        videoEl.onloadedmetadata = () => videoEl.play().then(res).catch(rej);
        videoEl.onerror = rej;
      });
      setIsCameraReady(true);

      const fm = buildFaceMesh(onFrame);
      fmRef.current = fm;
      let running = true;
      const loop = async () => {
        if (!running) return;
        if (videoEl.readyState >= 2)
          try { await fm.send({ image: videoEl }); } catch { /* ignore */ }
        if (running) rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
      streamRef.current._stop = () => { running = false; };
    } catch (err) { setCameraError(err); }
  }, [buildFaceMesh, onFrame]);

  // ── Flip camera ──────────────────────────────────────────────────────────────
  const flipCamera = useCallback(() => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    setLiveChecks(null);
    resetLiveness();
    startMobileCamera(next);
  }, [facingMode, startMobileCamera, resetLiveness]);

  // ── Start camera when step becomes 'camera' ──────────────────────────────────
  useEffect(() => {
    if (step !== 'camera') return;
    resetLiveness();
    const t = setTimeout(() => {
      startDesktopCamera();
    }, 100);
    return () => { clearTimeout(t); teardown(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── Capture — crops to guide box region ──────────────────────────────────────
  const handleCapture = useCallback(async () => {
    const videoEl = videoRef.current;
    if (!videoEl || !isCameraReady) return;

    const blinksAtCapture = blinkCountRef.current;

    const vidW = videoEl.videoWidth  || 640;
    const vidH = videoEl.videoHeight || 480;

    const srcX = BOX_X * vidW;
    const srcY = BOX_Y * vidH;
    const srcW = BOX_W * vidW;
    const srcH = BOX_H * vidH;

    // ── Canvas for DISPLAY (mirrored for front cam, as before) ──
    const displayCanvas = document.createElement('canvas');
    displayCanvas.width  = srcW;
    displayCanvas.height = srcH;
    const dCtx = displayCanvas.getContext('2d');
    if (facingMode === 'user') {
      dCtx.translate(displayCanvas.width, 0);
      dCtx.scale(-1, 1);
    }
    dCtx.drawImage(videoEl, srcX, srcY, srcW, srcH, 0, 0, displayCanvas.width, displayCanvas.height);

    // ── Canvas for ANALYSIS (never mirrored — FaceMesh expects natural orientation) ──
    const analysisCanvas = document.createElement('canvas');
    analysisCanvas.width  = srcW;
    analysisCanvas.height = srcH;
    const aCtx = analysisCanvas.getContext('2d');
    aCtx.drawImage(videoEl, srcX, srcY, srcW, srcH, 0, 0, analysisCanvas.width, analysisCanvas.height);

    const dataUri         = displayCanvas.toDataURL('image/jpeg', 0.92);
    const analysisDataUri = analysisCanvas.toDataURL('image/jpeg', 0.92);

    teardown();
    setIsCameraReady(false);
    setCapturedImage(dataUri);           // show the mirrored (natural-looking) image
    setUploadStatus('');
    setValidating(true);
    setStep('preview');

    displayCanvas.toBlob((blob) => setCapturedBlob(blob), 'image/jpeg', 0.92);

    try {
      // run FaceMesh on the UN-mirrored frame
      const checks = await runFaceMeshOnce(analysisDataUri);

      // Texture sample on display canvas (appearance) is fine
      const cx     = Math.round(displayCanvas.width  * 0.50);
      const cy     = Math.round(displayCanvas.height * 0.45);
      const radius = Math.max(10, Math.round(displayCanvas.width * 0.04));
      const stddev = textureSample(displayCanvas, cx, cy, radius);
      const tOk    = stddev === null ? true : stddev >= TEXTURE_MIN_STDDEV;
      setTextureOk(tOk);

      checks.livenessOk = checks.depthOk && tOk && blinksAtCapture >= requiredBlinks;
      setLiveChecks(checks);
      setBlinkCount(blinksAtCapture);
      blinkCountRef.current = blinksAtCapture;
    } catch { /* keep last liveChecks */ }
    finally { setValidating(false); }
  }, [isCameraReady, facingMode, teardown, requiredBlinks]);

  // ── Retake ───────────────────────────────────────────────────────────────────
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setCapturedBlob(null);
    setUploadStatus('');
    setLiveChecks(null);
    setIsCameraReady(false);
    resetLiveness();
    setStep('camera');
  }, [resetLiveness]);

  // ── Submit ───────────────────────────────────────────────────────────────────
  const submitImage = useCallback(async () => {
    if (!capturedBlob) return;
    if (!allPassed(liveChecks, blinkCount, requiredBlinks)) return;
    if (!liveChecks?.livenessOk) return;

    setIsUploading(true);
    setUploadStatus('');
    try {
      if (onSubmit) {
        await onSubmit(capturedBlob);
        return;
      }

      const authToken = token || localStorage.getItem(WORKER_TOKEN_KEY);
      if (!authToken) {
        setUploadStatus('Not authenticated. Please log in again.');
        return;
      }

      const fd = new FormData();
      fd.append('file', capturedBlob, 'selfie.jpg');
const res = await fetch(`${BASE_URL}/lenskart/profile-media`, {
  method: 'POST',
  headers: { Authorization: `Bearer ${authToken}` },
  body: fd,
});

const result = await res.json().catch(() => ({}));

if (res.ok && result.success) {
  setUploadStatus('success');
  if (result.worker?.profilePhotoUrl && typeof setUser === 'function') {
    setUser({ ...(user || {}), profilePhotoUrl: result.worker.profilePhotoUrl });
  }
  setTimeout(() => {
    onSuccess?.();
    handleClose();
  }, 1500);
} else {
  throw new Error(result.message || `Upload failed: ${res.status}`);
}
    } catch (e) {
      setUploadStatus(e.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capturedBlob, liveChecks, blinkCount, requiredBlinks, onSubmit, token, BASE_URL, setUser, user]);

  const handleClose = () => { teardown(); onClose?.(); };

  const getCameraErrorMsg = () => {
    const s = cameraError?.toString() ?? '';
    if (s.includes('NotAllowedError') || s.includes('Permission denied'))
      return 'Camera access was denied. Allow permission in browser settings and refresh.';
    if (s.includes('NotFoundError') || s.includes('DevicesNotFound'))
      return 'No camera found on this device.';
    if (s.includes('OverconstrainedError'))
      return 'Camera constraints could not be satisfied. Try switching cameras.';
    return 'Camera error. Please refresh and try again.';
  };

  // ── Derived state ─────────────────────────────────────────────────────────────
  const checks        = liveChecks ?? {};
  const posOk         = positionOk(checks);
  const liveSessionOk = allPassed(checks, blinkCount, requiredBlinks);
  const previewOk     = liveSessionOk && checks.livenessOk;
  const uploadFailed  = Boolean(uploadStatus) && uploadStatus !== 'success';

  // ── Auto-capture — once every check passes, wait a short moment (so the face
  // is steady and eyes are fully open) and then take the photo automatically.
  // If any check fails during the delay, liveSessionOk flips to false, the
  // effect cleans up, and the timer is cancelled.
  useEffect(() => {
    if (step !== 'camera' || !isCameraReady || !liveSessionOk) return;
    const t = setTimeout(() => handleCapture(), AUTO_CAPTURE_DELAY_MS);
    return () => clearTimeout(t);
  }, [step, isCameraReady, liveSessionOk, handleCapture]);

  const statusColor = !liveChecks ? '#94a3b8'
    : previewOk || liveSessionOk  ? '#22c55e'
    : '#ef4444';

  const videoStyle = {
    width: '100%', height: '100%', objectFit: 'cover', display: 'block',
    transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
  };

  const liveHint = !liveChecks
    ? 'Fit your full face inside the box'
    : !checks.faceDetected
    ? 'No face detected'
    : !checks.singleFace
    ? 'Only one face please'
    : !checks.depthOk
    ? '⚠ Photo detected — use your real face'
    : !posOk
    ? (!checks.faceInFrame  ? 'Move back — show full face'
       : !checks.goodDistance ? 'Adjust distance'
       :                        'Look straight at camera')
    : blinkCount < requiredBlinks
    ? `Blink naturally (${blinkCount}/${requiredBlinks})`
    : !checks.eyesOpen
    ? 'Open your eyes'
    : '✓ Hold still — capturing…';

  const needBlink = posOk && checks.depthOk && blinkCount < requiredBlinks;

  return (
<>
  <style>{`
    .selfie-modal {
      height: 100dvh;
      max-height: 100dvh;
      border-radius: 0;
    }
    .selfie-backdrop {
      align-items: flex-end;
      padding: 0;
    }
    @media (min-width: 640px) {
      .selfie-modal {
        height: auto;
        max-height: 96vh;
        border-radius: 1rem;
      }
      .selfie-backdrop {
        align-items: center;
        padding: 0.75rem;
      }
    }
  `}</style>

  <div
    className={`selfie-backdrop fixed inset-0 flex justify-center z-50 ${
      onSubmit ? 'bg-black bg-opacity-10 pointer-events-none' : 'bg-black bg-opacity-60'
    }`}
  >
    <div
      className={`selfie-modal w-full bg-white shadow-2xl relative overflow-hidden flex flex-col ${
        onSubmit ? 'pointer-events-auto' : ''
      }`}
      style={{ maxWidth: 540 }}
    >

      {/* ── Header ── */}
      <div className="px-5 py-4 border-b flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          {step !== 'instructions' && !(onSubmit && step === 'camera') && (
            <button
              onClick={step === 'preview' ? retakePhoto : () => setStep('instructions')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors mr-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
          )}
          <h2 className="text-lg font-bold text-gray-800">
            {step === 'instructions' ? 'Take Your Selfie'
             : step === 'camera'     ? 'Position Your Face'
             :                         'Review Photo'}
          </h2>
        </div>
        {!onSubmit && (
          <button onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        )}
      </div>

      {/* ── Instructions ── */}
      {step === 'instructions' && (
        <div className="overflow-y-auto flex-1">
          <InstructionsScreen onStart={() => setStep('camera')}/>
        </div>
      )}

      {/* ── Camera ── */}
      {step === 'camera' && (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">

          {/* Single error banner */}
          {liveChecks && !liveSessionOk && (
            <div className="shrink-0">
              <ErrorBanner checks={checks} blinkCount={blinkCount} requiredBlinks={requiredBlinks} />
            </div>
          )}

          {cameraError ? (
            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                <p className="font-semibold mb-1">Camera Error</p>
                <p>{getCameraErrorMsg()}</p>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
                <p className="font-semibold mb-1">How to fix:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Allow camera permission in browser / app settings</li>
                  <li>Refresh and try again</li>
                  <li>Settings → Browser → Camera → Allow</li>
                </ul>
              </div>
              <button onClick={() => window.location.reload()}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-xl transition-colors">
                ↻ Refresh Page
              </button>
            </div>
          ) : (
            <>
              {/* Video — grows to fill all available space */}
              <div
                className="relative w-full bg-black overflow-hidden"
                style={{ flex: 1, minHeight: 0 }}
              >
                <video ref={videoRef} autoPlay playsInline muted style={videoStyle}/>

                {/* SVG overlay */}
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 2 }}>
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none"
                    xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                    <defs>
                      <mask id="rectCutout">
                        <rect width="100" height="100" fill="white"/>
                        <rect x="8" y="5" width="84" height="88" rx="3" fill="black"/>
                      </mask>
                      <style>{`
                        @keyframes arrowPulse {
                          0%,100% { opacity: 0.4; }
                          50%      { opacity: 1.0; }
                        }
                        @keyframes nudgeUp    { 0%,100%{transform:translateY(0)}  50%{transform:translateY(-2px)} }
                        @keyframes nudgeDown  { 0%,100%{transform:translateY(0)}  50%{transform:translateY( 2px)} }
                        @keyframes nudgeLeft  { 0%,100%{transform:translateX(0)}  50%{transform:translateX(-2px)} }
                        @keyframes nudgeRight { 0%,100%{transform:translateX(0)}  50%{transform:translateX( 2px)} }
                        @keyframes nudgeIn    { 0%,100%{transform:scale(1)}       50%{transform:scale(0.86)}      }
                        @keyframes nudgeOut   { 0%,100%{transform:scale(1)}       50%{transform:scale(1.14)}      }
                        @keyframes nudgeTiltL { 0%,100%{transform:rotate(0deg)}   50%{transform:rotate(-8deg)}    }
                        @keyframes nudgeTiltR { 0%,100%{transform:rotate(0deg)}   50%{transform:rotate( 8deg)}    }
                        .a-up    { animation: arrowPulse 0.9s ease-in-out infinite, nudgeUp    0.9s ease-in-out infinite; transform-origin: 50px 2px;  }
                        .a-down  { animation: arrowPulse 0.9s ease-in-out infinite, nudgeDown  0.9s ease-in-out infinite; transform-origin: 50px 98px; }
                        .a-left  { animation: arrowPulse 0.9s ease-in-out infinite, nudgeLeft  0.9s ease-in-out infinite; transform-origin: 2px  50px; }
                        .a-right { animation: arrowPulse 0.9s ease-in-out infinite, nudgeRight 0.9s ease-in-out infinite; transform-origin: 98px 50px; }
                        .a-in    { animation: arrowPulse 0.9s ease-in-out infinite, nudgeIn    0.9s ease-in-out infinite; transform-origin: 50px 50px; }
                        .a-out   { animation: arrowPulse 0.9s ease-in-out infinite, nudgeOut   0.9s ease-in-out infinite; transform-origin: 50px 50px; }
                        .a-tiltL { animation: arrowPulse 0.9s ease-in-out infinite, nudgeTiltL 1.2s ease-in-out infinite; transform-origin: 50px 47px; }
                        .a-tiltR { animation: arrowPulse 0.9s ease-in-out infinite, nudgeTiltR 1.2s ease-in-out infinite; transform-origin: 50px 47px; }
                      `}</style>
                    </defs>

                    <rect width="100" height="100" fill="rgba(0,0,0,0.50)" mask="url(#rectCutout)"/>

                    <rect x="8" y="5" width="84" height="88" rx="3" fill="none"
                      stroke={statusColor} strokeWidth="1.5" style={{ transition: 'stroke 0.4s ease' }}/>

                    {[
                      [8,  5,   1,  1], [92, 5,  -1,  1],
                      [8,  93,  1, -1], [92, 93, -1, -1],
                    ].map(([x, y, dx, dy], i) => (
                      <g key={i}>
                        <line x1={x} y1={y} x2={x + dx * 8} y2={y}
                          stroke={statusColor} strokeWidth="2.5" strokeLinecap="round"
                          style={{ transition: 'stroke 0.4s ease' }}/>
                        <line x1={x} y1={y} x2={x} y2={y + dy * 8}
                          stroke={statusColor} strokeWidth="2.5" strokeLinecap="round"
                          style={{ transition: 'stroke 0.4s ease' }}/>
                      </g>
                    ))}

                    {(() => {
                      if (!liveChecks) return (
                        <g fill="rgba(255,255,255,0.65)">
                          <polygon className="a-up"    points="50,2  46.5,9  53.5,9"/>
                          <polygon className="a-down"  points="50,98 46.5,91 53.5,91"/>
                          <polygon className="a-left"  points="2,50  9,46.5  9,53.5"/>
                          <polygon className="a-right" points="98,50 91,46.5 91,53.5"/>
                        </g>
                      );

                      if (!liveChecks.faceDetected) return (
                        <>
                          <g fill="#facc15">
                            <polygon className="a-up"    points="50,2  46.5,9  53.5,9"/>
                            <polygon className="a-down"  points="50,98 46.5,91 53.5,91"/>
                            <polygon className="a-left"  points="2,50  9,46.5  9,53.5"/>
                            <polygon className="a-right" points="98,50 91,46.5 91,53.5"/>
                          </g>
                          <text x="50" y="97" textAnchor="middle" fontSize="3.8"
                            fill="rgba(255,220,0,0.95)" fontFamily="sans-serif">
                            centre your face in the box
                          </text>
                        </>
                      );

                      if (!liveChecks.singleFace) return (
                        <text x="50" y="97" textAnchor="middle" fontSize="3.8"
                          fill="#f87171" fontFamily="sans-serif">
                          only one person please
                        </text>
                      );

                      if (!liveChecks.faceInFrame) {
                        const nx = liveChecks._noseX ?? 0.5;
                        const ny = liveChecks._noseY ?? 0.5;
                        const goUp    = ny > 0.62;
                        const goDown  = ny < 0.30;
                        const goLeft  = nx > 0.62;
                        const goRight = nx < 0.38;
                        const fallback = !goUp && !goDown && !goLeft && !goRight;
                        const label = goUp ? 'move up' : goDown ? 'move down'
                          : goLeft ? 'move left' : goRight ? 'move right' : 'fit face in box';
                        return (
                          <>
                            <g fill="#facc15">
                              {(goUp    || fallback) && <polygon className="a-up"    points="50,2  46.5,9  53.5,9"/>}
                              {(goDown  || fallback) && <polygon className="a-down"  points="50,98 46.5,91 53.5,91"/>}
                              {(goLeft  || fallback) && <polygon className="a-left"  points="2,50  9,46.5  9,53.5"/>}
                              {(goRight || fallback) && <polygon className="a-right" points="98,50 91,46.5 91,53.5"/>}
                            </g>
                            <text x="50" y="97" textAnchor="middle" fontSize="3.8"
                              fill="rgba(255,220,0,0.95)" fontFamily="sans-serif">
                              {label}
                            </text>
                          </>
                        );
                      }

                      if (!liveChecks.goodDistance) {
                        const tooFar = !liveChecks._faceArea ||
                          liveChecks._faceArea < (FACE_COV_MIN + FACE_COV_MAX) / 2;
                        return tooFar ? (
                          <>
                            <g fill="#60a5fa" className="a-in">
                              <polygon points="50,20  46.5,27 53.5,27"/>
                              <polygon points="50,80  46.5,73 53.5,73"/>
                              <polygon points="22,50  29,46.5 29,53.5"/>
                              <polygon points="78,50  71,46.5 71,53.5"/>
                            </g>
                            <text x="50" y="97" textAnchor="middle" fontSize="3.8"
                              fill="#93c5fd" fontFamily="sans-serif">move closer</text>
                          </>
                        ) : (
                          <>
                            <g fill="#fb923c" className="a-out">
                              <polygon points="50,8   46.5,15 53.5,15"/>
                              <polygon points="50,92  46.5,85 53.5,85"/>
                              <polygon points="10,50  17,46.5 17,53.5"/>
                              <polygon points="90,50  83,46.5 83,53.5"/>
                            </g>
                            <text x="50" y="97" textAnchor="middle" fontSize="3.8"
                              fill="#fdba74" fontFamily="sans-serif">move back</text>
                          </>
                        );
                      }

                      if (!liveChecks.faceStraight) {
                        const tiltRight = (liveChecks._earDeltaY ?? 0) > 0;
                        return (
                          <>
                            {tiltRight ? (
                              <g fill="#c084fc">
                                <path className="a-tiltR" d="M38,16 A16,16 0 0,1 62,16"
                                  fill="none" stroke="#c084fc" strokeWidth="2" strokeLinecap="round"/>
                                <polygon points="62,16 58,10 65,12"/>
                              </g>
                            ) : (
                              <g fill="#c084fc">
                                <path className="a-tiltL" d="M62,16 A16,16 0 0,0 38,16"
                                  fill="none" stroke="#c084fc" strokeWidth="2" strokeLinecap="round"/>
                                <polygon points="38,16 35,10 42,12"/>
                              </g>
                            )}
                          </>
                        );
                      }

                      if (!liveChecks.depthOk) return (
                        <text x="50" y="97" textAnchor="middle" fontSize="3.8"
                          fill="#f87171" fontFamily="sans-serif">
                          use your real face
                        </text>
                      );

                      return (
                        <>
                          {liveSessionOk && (
                            <>
                              <circle cx="88" cy="9" r="5" fill="#22c55e"/>
                              <polyline points="85.5,9 87.5,11 90.5,7"
                                fill="none" stroke="white" strokeWidth="1.2"
                                strokeLinecap="round" strokeLinejoin="round"/>
                            </>
                          )}
                          <text x="50" y="97" textAnchor="middle" fontSize="3.8"
                            fill="rgba(255,255,255,0.9)" fontFamily="sans-serif">
                            {liveHint}
                          </text>
                        </>
                      );
                    })()}
                  </svg>
                </div>

                {!isCameraReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-75 z-10 gap-3">
                    <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-transparent"/>
                    <span className="text-white text-sm">Starting camera…</span>
                  </div>
                )}
              </div>

              {/* Status bar — pinned to bottom. Photo is taken automatically. */}
              <div
                className="shrink-0 bg-white px-5 pt-3 pb-5 flex flex-col gap-2"
                style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
              >
                <div
                  className={`w-full font-bold py-4 rounded-2xl text-base text-center transition-all duration-300
                    ${liveSessionOk
                      ? 'bg-green-500 text-white shadow-lg shadow-green-100'
                      : 'bg-gray-200 text-gray-400'}`}
                >
                  {!isCameraReady
                    ? 'Loading Camera…'
                    : needBlink
                    ? `Blink ${requiredBlinks - blinkCount} more time${requiredBlinks - blinkCount !== 1 ? 's' : ''}…`
                    : liveSessionOk
                    ? 'Hold still… capturing'
                    : 'Waiting…'}
                </div>
                <p className="text-xs text-gray-400 text-center">
                  Blink naturally to prove liveness · Photo is captured automatically
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Preview ── */}
      {step === 'preview' && (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">

          {/* Banners — shrink-0 so they never squish the image */}
          <div className="shrink-0">
            {!validating && liveChecks && !previewOk && (
              <div className="space-y-2 pt-2">
                <ErrorBanner checks={checks} blinkCount={blinkCount} requiredBlinks={requiredBlinks} />
                {!checks.livenessOk && (
                  <div className="mx-5 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-2.5 rounded-xl flex items-start gap-2 text-xs">
                    <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                    <p className="font-medium">
                      Liveness check failed — please retake using your real face and blink naturally
                    </p>
                  </div>
                )}
              </div>
            )}

            {uploadStatus === 'success' && (
              <div className="mx-5 mt-3 bg-green-50 border border-green-200 text-green-700 px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                Photo uploaded successfully!
              </div>
            )}
            {uploadFailed && (
              <div className="mx-5 mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-sm text-center">
                {uploadStatus}
              </div>
            )}
          </div>

          {/* Captured image — fills remaining space */}
          <div className="flex-1 min-h-0 px-5 pt-4">
            <div
              className="relative w-full h-full rounded-2xl overflow-hidden bg-black"
              style={{
                border: `4px solid ${validating ? '#94a3b8' : previewOk ? '#22c55e' : '#ef4444'}`,
                transition: 'border-color 0.4s ease',
              }}
            >
              <img
                src={capturedImage}
                alt="Captured selfie"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {validating && (
                <div className="absolute inset-0 bg-black bg-opacity-55 flex flex-col items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-white border-t-transparent"/>
                  <span className="text-white text-sm font-medium">Analysing…</span>
                </div>
              )}
              {!validating && uploadStatus === 'success' && (
                <div className="absolute bottom-4 right-4 bg-green-500 rounded-full p-2 shadow-lg">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Bottom action bar — Retake + OK when the photo passed all checks,
              Retake only when something needs fixing. OK submits the photo. */}
          <div
            className="shrink-0 px-5 pt-3 pb-5 flex gap-3"
            style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
          >
            {previewOk ? (
              uploadStatus === 'success' ? (
                <div className="flex-1 bg-blue-50 text-blue-600 font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2">
                  Uploaded ✓
                </div>
              ) : (
                <>
                  <button
                    onClick={retakePhoto}
                    disabled={isUploading}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-semibold py-3.5 rounded-2xl transition-colors"
                  >
                    Retake
                  </button>
                  <button
                    onClick={submitImage}
                    disabled={isUploading}
                    className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-semibold py-3.5 rounded-2xl transition-colors flex items-center justify-center gap-2"
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Submitting…
                      </>
                    ) : (
                      'OK'
                    )}
                  </button>
                </>
              )
            ) : (
              <button
                onClick={retakePhoto}
                disabled={isUploading || validating}
                className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-semibold py-3.5 rounded-2xl transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Retake
              </button>
            )}
          </div>
        </div>
      )}

      {onSubmit && (
        <div className="shrink-0 flex justify-center py-3 border-t bg-white">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-xs text-indigo-500 hover:text-indigo-700 underline transition-colors shrink-0"
          >
            <QrCode size={13}/> Switch to QR Code instead
          </button>
        </div>
      )}

    </div>
  </div>
</>
  );
};

export default SelfieImage;